import React, { useCallback, useState } from 'react';
import { ErrorApi, AlertApi } from '@backstage/core-plugin-api';
import { ExecuteActionOptions } from '../../../api';
import { MenuPosition } from '../../types';
import {
  GCPActionType,
  GCPResourceType,
  ExecuteActionResponse,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { CRDGCPConfig } from '@internal/backstage-plugin-compute-resource-discovery-new-common';

interface UseActionManagementProps {
  executeAction: (
    options: ExecuteActionOptions,
  ) => Promise<ExecuteActionResponse>;
  refresh: () => void;
  gcpConfig: CRDGCPConfig;
  errorApi: ErrorApi;
  alertApi: AlertApi;
  entityRef: string;
  projectCode: string;
}

export const useActionManagement = ({
  executeAction,
  refresh,
  errorApi,
  alertApi,
  gcpConfig,
  entityRef,
  projectCode,
}: UseActionManagementProps) => {
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    mouseX: null,
    mouseY: null,
  });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [rdsConnectionDialogOpen, setRdsConnectionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<GCPActionType | null>(
    null,
  );
  const [service, setService] = useState<GCPResourceType | null>(null);
  const [isActionInProgress, setActionInProgress] = useState<boolean>(false);

  // Handle opening the action menu from table-level action
  const handleTableActionClick = useCallback((event: React.MouseEvent) => {
    setMenuPosition({
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuPosition({
      mouseX: null,
      mouseY: null,
    });
  }, []);

  const handleActionClick = useCallback(
    (action: GCPActionType, service: GCPResourceType) => {
      setSelectedAction(action);
      setService(service);
      setConfirmationDialogOpen(true);
      handleMenuClose();
    },
    [handleMenuClose],
  );

  const handleOpenConnection = useCallback(() => {
    setRdsConnectionDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleConfirmAction = useCallback(async () => {
    if (!selectedAction || !service) return;

    setConfirmationDialogOpen(false);
    setActionInProgress(true);

    try {
      const response = await executeAction({
        serviceType: 'gcp',
        entityRef,
        gcp: {
          action: selectedAction,
          resourceType: service,
          projectId: gcpConfig.projectId,
          projectCode,
        },
      });

      if (response.status === 'success') {
        alertApi.post({
          message: `Action ${selectedAction} for ${service} has been initiated. ${response.response?.message}`,
          severity: 'success',
        });
        refresh();
      } else {
        throw new Error(response.error || 'Unknown error');
      }
    } catch (error) {
      errorApi.post(
        new Error(`Error to execute action ${selectedAction} for ${service}`),
      );
    } finally {
      setActionInProgress(false);
      setSelectedAction(null);
      setService(null);
    }
  }, [
    selectedAction,
    executeAction,
    refresh,
    gcpConfig,
    projectCode,
    entityRef,
  ]);

  return {
    menuPosition,
    confirmationDialogOpen,
    rdsConnectionDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    handleOpenConnection,
    setConfirmationDialogOpen,
    setRdsConnectionDialogOpen,
  };
};
